// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    makeStyles,
    shorthands,
    TableColumnDefinition,
    TableColumnSizingOptions,
    tokens,
} from '@fluentui/react-components';
import { Image20Regular } from '@fluentui/react-icons';
import { compareDates, compareLocale, Curriculum } from '@microsoft/vscodeedu-api';
import React, { useContext, useEffect, useMemo } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getOpenCourseUrl, getUserId, useConfig, UserContext } from '../../data';
import { getOpenCurriculumUrl } from '../../data/curriculum-context';
import { useTheme } from '../../data/theme-context';
import { useAppStyles } from '../../styles';
import { FilterPill, getCoursePlatformText, getCourseTypeText, IntlProps, TrackContext } from '../common';
import { getCurriculumStateText } from './curriculum-state-text';
import EmptyState from './empty-state';
import ImageCell from './image-cell';
import LastPublishedCell from './last-published-cell';
import PlatformCell from './platform-cell';
import StatusCell from './status-cell';
import TitleCell from './title-cell';
import TypeCell from './type-cell';

// Curriculum list properties.
export type PublishedProjectListProps = IntlProps<{
    items: ReadonlyArray<Curriculum>;
    onCreate: () => void;
    onPublish: (curriculumId: string) => void;
    onUnpublish: (curriculumId: string) => void;
    onDelete: (curriculumId: string) => void;
}>;

const columnSizingOptions: TableColumnSizingOptions = {
    image: {
        defaultWidth: 220,
        idealWidth: 220,
    },
    title: {
        idealWidth: 500,
        defaultWidth: 500,
    },
    type: {
        idealWidth: 100,
        defaultWidth: 100,
    },
    platform: {
        idealWidth: 400,
        defaultWidth: 400,
    },
    status: {
        idealWidth: 200,
        defaultWidth: 200,
    },
    lastPublished: {
        idealWidth: 200,
        defaultWidth: 200,
    },
};

const useStyles = makeStyles({
    header: {
        fontWeight: tokens.fontWeightSemibold,
    },
    row: {
        ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStrokeAlpha),
        paddingTop: '13px',
        paddingBottom: '13px',
        '&:focus-within button': {
            display: 'unset',
        },
    },
    cell: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'flex-start',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
});

// Curriculum data grid.
export default injectIntl((props: PublishedProjectListProps) => {
    const { intl, items, onCreate, onPublish, onUnpublish, onDelete } = props;
    const appStyles = useAppStyles();
    const styles = useStyles();
    const [userStore] = useContext(UserContext);
    const userId = getUserId(userStore);

    const { courseProfileName } = useConfig();
    const [{ profileTheme }] = useTheme();

    const [typeFilter, setTypeFilter] = React.useState<string[]>([]);
    const typeFilterOptions: [string, string][] = useMemo(
        () => [
            ['course', getCourseTypeText(intl, 'course')],
            ['coding-activity', getCourseTypeText(intl, 'coding-activity')],
        ],
        [intl]
    );

    const [platformFilter, setPlatformFilter] = React.useState<string[]>([]);
    const platformFilterOptions: [string, string][] = useMemo(
        () => [
            ['python', getCoursePlatformText('python')],
            ['html', getCoursePlatformText('html')],
        ],
        []
    );

    const [stateFilter, setStateFilter] = React.useState<string[]>([]);
    const stateFilterOptions: [string, string][] = useMemo(
        () => [
            ['draft', getCurriculumStateText(intl, 'draft')],
            ['published', getCurriculumStateText(intl, 'published')],
        ],
        [intl]
    );

    // Filter items in response to filter pill changes.
    const [filteredItems, setFilteredItems] = React.useState<readonly Curriculum[]>(items);
    useEffect(() => {
        setFilteredItems(
            items.filter(
                (item) =>
                    (typeFilter.length === 0 || typeFilter.includes(item.type)) &&
                    (platformFilter.length === 0 || platformFilter.includes(item.platform)) &&
                    (stateFilter.length === 0 || stateFilter.includes(item.state))
            )
        );
    }, [items, typeFilter, platformFilter, stateFilter]);

    const columns = useMemo<TableColumnDefinition<Curriculum>[]>(
        () => [
            createTableColumn({
                columnId: 'image',
                renderHeaderCell: () => <Image20Regular />,
                renderCell: (item) => <ImageCell {...item} />,
            }),
            createTableColumn({
                columnId: 'title',
                compare: (a, b) => compareLocale(a.title, b.title),
                renderHeaderCell: () => (
                    <FormattedMessage defaultMessage="Title" description="Column title for activity data grid." />
                ),
                renderCell: (item) => {
                    const { curriculumId, courseId, repoName } = item;
                    const titleLinkUrl = getOpenCurriculumUrl(curriculumId, repoName, userId);
                    const courseLinkUrl = getOpenCourseUrl(
                        courseId,
                        courseProfileName,
                        profileTheme,
                        userId
                    )?.toString();
                    return (
                        <TitleCell
                            {...item}
                            titleLinkUrl={titleLinkUrl}
                            courseLinkUrl={courseLinkUrl}
                            courseId={item.courseId}
                            onPublish={() => onPublish(item.curriculumId)}
                            onUnpublish={() => onUnpublish(item.curriculumId)}
                            onDelete={() => onDelete(item.curriculumId)}
                        />
                    );
                },
            }),
            createTableColumn({
                columnId: 'type',
                compare: (a, b) => compareLocale(getCourseTypeText(intl, a.type), getCourseTypeText(intl, b.type)),
                renderHeaderCell: () => (
                    <FormattedMessage defaultMessage="Type" description="Column title for activity data grid." />
                ),
                renderCell: (item) => <TypeCell value={item.type} />,
            }),
            createTableColumn({
                columnId: 'platform',
                compare: (a, b) => compareLocale(getCoursePlatformText(a.platform), getCoursePlatformText(b.platform)),
                renderHeaderCell: () => (
                    <FormattedMessage defaultMessage="Platform" description="Column title for activity data grid." />
                ),
                renderCell: (item) => <PlatformCell value={item.platform} />,
            }),
            createTableColumn({
                columnId: 'status',
                compare: (a, b) =>
                    compareLocale(getCurriculumStateText(intl, a.state), getCurriculumStateText(intl, b.state)),
                renderHeaderCell: () => (
                    <FormattedMessage defaultMessage="Status" description="Column title for activity data grid." />
                ),
                renderCell: (item) => <StatusCell value={item.state} />,
            }),
            createTableColumn({
                columnId: 'lastPublished',
                compare: (a, b) => compareDates(a.lastPublished, b.lastPublished),
                renderHeaderCell: () => (
                    <FormattedMessage
                        defaultMessage="Last published"
                        description="Column title for activity data grid."
                    />
                ),
                renderCell: (item) => <LastPublishedCell {...item} />,
            }),
        ],
        [courseProfileName, intl, onDelete, onPublish, onUnpublish, profileTheme, userId]
    );

    return (
        <TrackContext.Provider value={{ container: 'curriculum-list' }}>
            {items.length === 0 && <EmptyState onCreate={onCreate} />}
            {items.length > 0 && (
                <div className={appStyles.flexColumnLargeGap}>
                    <div className={appStyles.flexRowSmallGap}>
                        <FilterPill
                            title={<FormattedMessage defaultMessage="Type" description="Filter bubble name." />}
                            options={typeFilterOptions}
                            onChanged={setTypeFilter}
                        />
                        <FilterPill
                            title={<FormattedMessage defaultMessage="Platform" description="Filter bubble name." />}
                            options={platformFilterOptions}
                            onChanged={setPlatformFilter}
                        />
                        <FilterPill
                            title={<FormattedMessage defaultMessage="Status" description="Filter bubble name." />}
                            options={stateFilterOptions}
                            onChanged={setStateFilter}
                        />
                    </div>
                    <DataGrid
                        items={filteredItems as Curriculum[]}
                        getRowId={(item) => item.curriculumId}
                        columns={columns}
                        columnSizingOptions={columnSizingOptions}
                        resizableColumns
                        sortable
                        defaultSortState={{ sortColumn: 'title', sortDirection: 'ascending' }}
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell }) => (
                                    <DataGridHeaderCell className={styles.header}>
                                        {renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<Curriculum>>
                            {({ item, rowId }) => (
                                <DataGridRow<Curriculum> key={rowId} className={styles.row}>
                                    {({ renderCell }) => (
                                        <DataGridCell className={styles.cell}>
                                            <TrackContext.Provider
                                                value={{
                                                    container: 'activity-list-row',
                                                    props: { curriculumId: item.curriculumId },
                                                }}
                                            >
                                                {renderCell(item)}
                                            </TrackContext.Provider>
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            )}
        </TrackContext.Provider>
    );
});
